/**
 * Card + KPI
 *
 * Card is the surface primitive — flat by default, hairline border, no shadow.
 * Use `padding` for the common inset value, or wrap children in a custom
 * layout yourself.
 *
 * KPI is the dashboard stat card. Used in 4-up strips at the top of every
 * data-heavy page (Finance, Properties, Dashboard).
 */

import React from 'react';
import { TrendingUp, TrendingDown } from './icons';

export function Card({
  padding = 0,
  surface = 'white',         // white | muted | sunk
  border = true,
  rounded = 'lg',
  hoverable = false,
  as: Tag = 'div',
  className = '',
  children,
  ...rest
}) {
  const surfaceClass = {
    white: 'bg-white',
    muted: 'bg-stone-100',
    sunk:  'bg-stone-50',
  }[surface];
  const roundedClass = { sm: 'rounded-sm', md: 'rounded-md', lg: 'rounded-lg', xl: 'rounded-xl' }[rounded];
  return (
    <Tag
      className={[
        surfaceClass,
        border && 'border border-stone-200',
        roundedClass,
        hoverable && 'transition-colors hover:border-stone-300',
        className,
      ].filter(Boolean).join(' ')}
      style={padding ? { padding } : undefined}
      {...rest}
    >
      {children}
    </Tag>
  );
}

/**
 * KPI — labelled stat with optional delta and sub-line.
 *
 *   <KPI label="Doanh thu tháng" value={VND(284500000)} delta="+12.4%" deltaUp
 *        sub="so với tháng trước" icon={<Wallet />} accent="primary" />
 *
 * `accent` controls the icon tile colour. Reserve for cases where the metric's
 * domain matters (overdue → danger, occupancy → primary). Skip otherwise.
 */
export function KPI({
  label,
  value,
  delta,
  deltaUp = true,
  sub,
  icon,
  accent,                  // primary | success | warning | danger | info | neutral
  className = '',
  ...rest
}) {
  const accentClasses = {
    primary:  'bg-teal-50  text-teal-700',
    success:  'bg-green-50  text-green-700',
    warning:  'bg-amber-50  text-amber-700',
    danger:   'bg-red-50   text-red-700',
    info:     'bg-blue-50     text-blue-700',
    neutral:  'bg-stone-100   text-stone-600',
  }[accent || 'neutral'];

  return (
    <div
      className={['bg-white border border-stone-200 rounded-lg p-3.5 flex flex-col gap-1.5 min-w-0', className].join(' ')}
      {...rest}
    >
      <div className="flex items-center justify-between gap-2">
        <span className="text-2xs font-semibold uppercase tracking-wider text-stone-500 truncate-line">{label}</span>
        {icon && (
          <span className={['shrink-0 w-6 h-6 rounded-md grid place-items-center', accentClasses].join(' ')}>
            {React.isValidElement(icon)
              ? React.cloneElement(icon, { size: icon.props.size ?? 14, 'aria-hidden': true })
              : icon}
          </span>
        )}
      </div>
      <div className="num text-2xl font-semibold text-stone-900 leading-none -tracking-tight">{value}</div>
      {(delta || sub) && (
        <div className="flex items-center gap-1.5 text-xs text-stone-600">
          {delta && (
            <span className={['num inline-flex items-center gap-0.5 font-medium',
              deltaUp ? 'text-green-600' : 'text-red-600'].join(' ')}>
              {deltaUp ? <TrendingUp size={12} /> : <TrendingDown size={12} />}
              {delta}
            </span>
          )}
          {sub && <span className="truncate-line">{sub}</span>}
        </div>
      )}
    </div>
  );
}

/** KPIStrip — 4-up by default, responsive. Drop in <KPI> children. */
export function KPIStrip({ columns = 4, className = '', children }) {
  const cols = {
    2: 'grid-cols-1 sm:grid-cols-2',
    3: 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-3',
    4: 'grid-cols-2 lg:grid-cols-4',
    5: 'grid-cols-2 lg:grid-cols-5',
  }[columns] || 'grid-cols-2 lg:grid-cols-4';
  return <div className={['grid gap-3', cols, className].join(' ')}>{children}</div>;
}
